// ChallengeLayout — iOS HIG (Human Interface Guidelines) chrome
// - Top: navigation bar (44pt, blur, back chevron, centered title, right action)
// - Center: large title + section header + inset-grouped content
// - Bottom: toolbar with primary button (safe-area aware)
// No progress bar — step indicator lives inside the large title area as 4 dots.

import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { BrandLogo } from '../../../components/Brand';

export type StepKey = 'tier' | 'size' | 'platform' | 'checkout';

const STEP_LABELS: Record<StepKey, { nav: string; title: string; sub: string }> = {
  tier:     { nav: 'Tier',     title: 'Choose tier',     sub: 'Beginner, Mid or Professional' },
  size:     { nav: 'Size',     title: 'Choose size',     sub: 'Pick your funded account size' },
  platform: { nav: 'Platform', title: 'Choose platform', sub: 'Pick your trading platform' },
  checkout: { nav: 'Checkout', title: 'Review & pay',    sub: 'Confirm and complete payment' },
};

const STEP_ORDER: StepKey[] = ['tier', 'size', 'platform', 'checkout'];

export function stepIndex(s: StepKey): number {
  return STEP_ORDER.indexOf(s);
}
export function nextStep(s: StepKey): StepKey | null {
  const i = stepIndex(s);
  return i < STEP_ORDER.length - 1 ? STEP_ORDER[i + 1] : null;
}
export function prevStep(s: StepKey): StepKey | null {
  const i = stepIndex(s);
  return i > 0 ? STEP_ORDER[i - 1] : null;
}
export function stepPath(s: StepKey): string {
  if (s === 'tier') return '/portal/challenge/tier';
  if (s === 'size') return '/portal/challenge/size';
  if (s === 'platform') return '/portal/challenge/platform';
  return '/portal/challenge/checkout';
}

interface LayoutProps {
  current: StepKey;
  children: ReactNode;
  footer?: ReactNode;
  /** Optional custom large title (defaults to step default) */
  largeTitle?: string;
  /** Optional custom subtitle */
  subtitle?: string;
}

export function ChallengeLayout({ current, children, footer, largeTitle, subtitle }: LayoutProps) {
  const nav = useNavigate();
  const idx = stepIndex(current);
  const meta = STEP_LABELS[current];

  return (
    <div className="min-h-screen bg-[#F2F2F7] text-[#1C1C1E] antialiased">
      {/* iOS Navigation Bar — 44pt, blur, hairline bottom */}
      <header
        className="sticky top-0 z-40 border-b border-[#C6C6C8]/60 bg-white/85 backdrop-blur-xl"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="relative flex h-11 items-center justify-between px-2">
          <button
            onClick={() => {
              const p = prevStep(current);
              if (p) nav(stepPath(p));
              else nav('/portal/dashboard');
            }}
            className="flex h-9 items-center gap-0.5 rounded-md px-2 text-[17px] font-normal text-[#007AFF] active:opacity-40"
            data-testid="challenge-back"
          >
            <svg width="12" height="20" viewBox="0 0 12 20" fill="none" aria-hidden>
              <path d="M10.5 1.5L2 10l8.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>{prevStep(current) ? STEP_LABELS[prevStep(current)!].nav : 'Dashboard'}</span>
          </button>
          <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[17px] font-semibold tracking-tight text-[#1C1C1E]">
            {meta.nav}
          </div>
          <button
            onClick={() => nav('/portal/dashboard')}
            className="flex h-9 items-center rounded-md px-2 text-[17px] font-normal text-[#007AFF] active:opacity-40"
          >
            Cancel
          </button>
        </div>
      </header>

      {/* Large title + 4-dot step indicator */}
      <div className="bg-[#F2F2F7] px-5 pb-3 pt-2">
        <h1 className="text-[34px] font-bold leading-[1.1] tracking-tight text-[#1C1C1E]">
          {largeTitle ?? meta.title}
        </h1>
        {subtitle !== null && (
          <p className="mt-1 text-[15px] text-[#8E8E93]">{subtitle ?? meta.sub}</p>
        )}
        <div className="mt-3 flex items-center gap-1.5" data-testid="step-dots" data-step={current}>
          {STEP_ORDER.map((_, i) => (
            <div
              key={i}
              className={[
                'h-1 flex-1 rounded-full transition-colors',
                i <= idx ? 'bg-[#1C1C1E]' : 'bg-[#C7C7CC]',
              ].join(' ')}
            />
          ))}
        </div>
      </div>

      {/* Content area */}
      <main
        className="mx-auto w-full max-w-[640px] px-4 pb-[calc(88px+env(safe-area-inset-bottom))] pt-4 md:px-6"
      >
        {children}
      </main>

      {/* Bottom toolbar */}
      {footer && (
        <div
          className="fixed inset-x-0 bottom-0 z-40 border-t border-[#C6C6C8]/60 bg-white/90 backdrop-blur-xl"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <div className="mx-auto w-full max-w-[640px] px-4 py-3 md:px-6">{footer}</div>
        </div>
      )}
    </div>
  );
}

// ─── iOS-styled building blocks ─────────────────────────────────────────────

/** Section header: small uppercase, secondary text, padded like iOS */
export function IOSSectionHeader({ children }: { children: ReactNode }) {
  return (
    <h2 className="px-4 pb-2 pt-6 text-[13px] font-normal uppercase tracking-wide text-[#8E8E93]">
      {children}
    </h2>
  );
}

/** Inset-grouped list — white rounded card with hairline dividers between rows */
export function IOSList({ children }: { children: ReactNode }) {
  return (
    <div className="overflow-hidden rounded-[12px] bg-white">
      {children}
    </div>
  );
}

interface IOSRowProps {
  /** Optional left accessory (icon, avatar) */
  leading?: ReactNode;
  /** Primary title */
  title: ReactNode;
  /** Optional secondary text below title */
  subtitle?: ReactNode;
  /** Right accessory (checkmark, chevron, badge, value) */
  trailing?: ReactNode;
  /** Whether the row is in selected state (subtle blue tint + checkmark) */
  selected?: boolean;
  onClick?: () => void;
  /** Show or hide the bottom hairline */
  showDivider?: boolean;
  /** Optional 3rd line (e.g. for small metadata) */
  footer?: ReactNode;
  testId?: string;
}

export function IOSRow({
  leading, title, subtitle, trailing, selected, onClick, showDivider = true, footer, testId,
}: IOSRowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={[
        'group relative flex w-full items-center gap-3 px-4 py-3 text-left transition-colors',
        onClick ? 'active:bg-[#E5E5EA]/60' : '',
        selected ? 'bg-[#007AFF]/[0.08]' : '',
      ].join(' ')}
    >
      {leading && <div className="shrink-0 self-start pt-0.5">{leading}</div>}
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <div className="text-[17px] leading-[1.3] text-[#1C1C1E]">{title}</div>
        </div>
        {subtitle && <div className="mt-0.5 text-[13px] leading-[1.3] text-[#8E8E93]">{subtitle}</div>}
        {footer && <div className="mt-1 text-[13px] text-[#8E8E93]">{footer}</div>}
      </div>
      {trailing && <div className="shrink-0 self-center">{trailing}</div>}
      {showDivider && (
        <div className="pointer-events-none absolute bottom-0 right-0 left-[60px] h-px bg-[#C6C6C8]/55" />
      )}
    </button>
  );
}

/** iOS checkmark badge (filled Apple-blue circle with white check) */
export function IOSCheck({ selected }: { selected: boolean }) {
  if (selected) {
    return (
      <div className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-[#007AFF]">
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 text-white" fill="none" stroke="currentColor" strokeWidth="3.5">
          <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    );
  }
  return <div className="h-[22px] w-[22px] rounded-full border-[1.5px] border-[#C7C7CC]" />;
}

/** iOS filled (black) primary button */
export function IOSPrimaryButton({
  children,
  onClick,
  disabled,
  type = 'button',
  loading,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit';
  loading?: boolean;
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      data-testid="challenge-continue"
      className="flex h-[50px] w-full items-center justify-center gap-2 rounded-[12px] bg-[#1C1C1E] text-[17px] font-semibold text-white transition active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-30"
    >
      {loading ? (
        <>
          <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="40" strokeDashoffset="20" />
          </svg>
          <span>Working…</span>
        </>
      ) : (
        children
      )}
    </button>
  );
}

/** iOS outlined (gray) secondary button */
export function IOSSecondaryButton({
  children, onClick, type = 'button',
}: {
  children: ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit';
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      className="flex h-[50px] w-full items-center justify-center gap-2 rounded-[12px] bg-[#F2F2F7] text-[17px] font-semibold text-[#1C1C1E] transition active:scale-[0.99]"
    >
      {children}
    </button>
  );
}
